import React, { useState, useEffect, useMemo } from 'react';
import { useGridCopyMenu } from './gridCommon';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  type ColDef,
  ModuleRegistry,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { getMainAxios } from '../utils/util';
import StrategySlotManager from './StrategySlotManager';
import { useAuth } from '../context/AuthContext';

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule]);

interface QuantStrategy {
  id: number;
  code: string;
  name: string;
  type: 'daily' | 'intraday';
  description: string | null;
  defaultParams: string | null;
  enabled: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

interface EditForm {
  id: number | null;
  code: string;
  name: string;
  type: 'daily' | 'intraday';
  description: string;
  defaultParams: string;
  enabled: boolean;
}

const emptyForm: EditForm = {
  id: null,
  code: '',
  name: '',
  type: 'daily',
  description: '',
  defaultParams: '',
  enabled: true,
};

const StrategyManagement: React.FC = () => {
  const copyMenu = useGridCopyMenu();
  const { isDev } = useAuth();
  const [rows, setRows] = useState<QuantStrategy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<'ALL' | 'daily' | 'intraday'>('ALL');
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<EditForm>(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchRows = async () => {
    setLoading(true);
    setError(null);
    try {
      const axios = getMainAxios();
      const params = typeFilter !== 'ALL' ? { type: typeFilter } : {};
      const res = await axios.get('/api/v1/quant/strategies', { params });
      setRows(res.data as QuantStrategy[]);
    } catch (err: any) {
      setError(err.response?.data?.error || '전략 목록 조회 실패');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRows(); }, [typeFilter]);

  const openCreate = () => {
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (row: QuantStrategy) => {
    setForm({
      id: row.id,
      code: row.code,
      name: row.name,
      type: row.type,
      description: row.description ?? '',
      defaultParams: row.defaultParams ?? '',
      enabled: row.enabled,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.code.trim()) { alert('code는 필수입니다.'); return; }
    if (!form.name.trim()) { alert('name은 필수입니다.'); return; }
    if (form.defaultParams.trim()) {
      try { JSON.parse(form.defaultParams); }
      catch { alert('defaultParams는 유효한 JSON이어야 합니다.'); return; }
    }

    setSaving(true);
    try {
      const axios = getMainAxios();
      await axios.post('/api/v1/quant/strategies', {
        id: form.id,
        code: form.code.trim(),
        name: form.name.trim(),
        type: form.type,
        description: form.description.trim() || null,
        defaultParams: form.defaultParams.trim() || null,
        enabled: form.enabled,
      });
      setModalOpen(false);
      fetchRows();
    } catch (err: any) {
      alert(err.response?.data?.error || '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: QuantStrategy) => {
    if (!confirm(`전략 '${row.code} - ${row.name}'을(를) 삭제하시겠습니까?`)) return;
    try {
      const axios = getMainAxios();
      await axios.delete(`/api/v1/quant/strategies/${row.id}`);
      fetchRows();
    } catch (err: any) {
      alert(err.response?.data?.error || '삭제 실패');
    }
  };

  const columnDefs = useMemo<ColDef<QuantStrategy>[]>(() => [
    { field: 'id', headerName: 'ID', width: 60, cellStyle: { textAlign: 'right' } },
    { field: 'code', headerName: '코드', width: 80, cellStyle: { textAlign: 'center', fontWeight: 600 } },
    { field: 'name', headerName: '이름', width: 160 },
    {
      field: 'type', headerName: '타입', width: 100, cellStyle: { textAlign: 'center' },
      cellRenderer: (p: any) => {
        const isDaily = p.value === 'daily';
        return (
          <span style={{
            padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
            backgroundColor: isDaily ? '#dbeafe' : '#fef3c7',
            color: isDaily ? '#1e40af' : '#92400e',
          }}>{isDaily ? '일봉' : '인트라데이'}</span>
        );
      },
    },
    { field: 'description', headerName: '설명', flex: 1, minWidth: 200 },
    {
      field: 'defaultParams', headerName: '기본파라미터', width: 260,
      cellStyle: { fontFamily: 'monospace', fontSize: '11px', color: '#6b7280' },
      valueFormatter: p => p.value ?? '',
    },
    {
      field: 'enabled', headerName: '활성', width: 70, cellStyle: { textAlign: 'center' },
      cellRenderer: (p: any) => p.value
        ? <span style={{ color: '#16a34a', fontWeight: 600 }}>●</span>
        : <span style={{ color: '#9ca3af' }}>○</span>,
    },
    {
      headerName: '작업', width: 130, cellStyle: { textAlign: 'center' },
      // 전략은 전 계좌 공용 — 추가·수정·삭제는 Dev 만 (서버 AdminPolicy, 2026-10-27)
      cellRenderer: (p: any) => !isDev ? <span style={{ color: '#9ca3af', fontSize: '11px' }}>조회 전용</span> : (
        <span>
          <button onClick={() => openEdit(p.data)}
            style={{ padding: '2px 8px', marginRight: '4px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '11px' }}>
            수정
          </button>
          <button onClick={() => handleDelete(p.data)}
            style={{ padding: '2px 8px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '11px' }}>
            삭제
          </button>
        </span>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [isDev]);

  return (
    <div style={{ padding: '20px', backgroundColor: '#eff6ff', minHeight: '100vh' }}>
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ color: '#1e40af', fontSize: '12px', fontWeight: 600 }}>전략 목록 ({rows.length}개)</span>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value as any)}
            style={{ padding: '4px 8px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '3px' }}>
            <option value="ALL">전체</option>
            <option value="daily">일봉</option>
            <option value="intraday">인트라데이</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {isDev && <button onClick={openCreate}
            style={{ padding: '5px 12px', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px', fontWeight: 500 }}>
            + 전략 추가
          </button>}
          <button onClick={fetchRows}
            style={{ padding: '5px 12px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px', fontWeight: 500 }}>
            새로고침
          </button>
        </div>
      </div>

      {error && (
        <div style={{ marginBottom: '10px', padding: '8px 12px', backgroundColor: '#fee2e2', border: '1px solid #ef4444', borderRadius: '4px', color: '#991b1b', fontSize: '12px' }}>
          {error}
        </div>
      )}

      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', overflow: 'hidden' }}>
        <div className="ag-theme-quartz" style={{ height: 520, width: '100%' }}>
          <AgGridReact<QuantStrategy> {...copyMenu.gridProps}
            rowData={rows}
            columnDefs={columnDefs}
            defaultColDef={{ resizable: true, sortable: true, filter: false, cellStyle: { fontSize: '12px' } }}
            getRowId={p => String(p.data.id)}
            animateRows={true}
            loading={loading}
          />
        </div>
      </div>

      <StrategySlotManager />

      {modalOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => !saving && setModalOpen(false)}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', width: '520px', maxHeight: '90vh', overflow: 'auto', border: '2px solid #3b82f6' }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', color: '#1e40af', fontSize: '14px', fontWeight: 600 }}>
              {form.id ? '전략 수정' : '전략 추가'}
            </h3>
            <div style={{ display: 'grid', gap: '10px', fontSize: '12px' }}>
              <label>
                <div style={{ marginBottom: '3px', fontWeight: 600 }}>코드 *</div>
                <input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })}
                  placeholder="1, 2, I1, I2 등"
                  style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '12px' }} />
              </label>
              <label>
                <div style={{ marginBottom: '3px', fontWeight: 600 }}>이름 *</div>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="MA Cross, RSI 등"
                  style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '12px' }} />
              </label>
              <label>
                <div style={{ marginBottom: '3px', fontWeight: 600 }}>타입 *</div>
                <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value as 'daily' | 'intraday' })}
                  style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '12px' }}>
                  <option value="daily">daily (일봉)</option>
                  <option value="intraday">intraday (인트라데이)</option>
                </select>
              </label>
              <label>
                <div style={{ marginBottom: '3px', fontWeight: 600 }}>설명</div>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '12px', resize: 'vertical' }} />
              </label>
              <label>
                <div style={{ marginBottom: '3px', fontWeight: 600 }}>기본 파라미터 (JSON)</div>
                <textarea value={form.defaultParams} onChange={e => setForm({ ...form, defaultParams: e.target.value })}
                  rows={5}
                  placeholder='{"period": 14, "threshold": 0.7}'
                  style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '3px', fontFamily: 'monospace', fontSize: '11px', resize: 'vertical' }} />
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input type="checkbox" checked={form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} />
                <span style={{ fontWeight: 600 }}>활성화</span>
              </label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button onClick={() => setModalOpen(false)} disabled={saving}
                style={{ padding: '6px 14px', backgroundColor: '#9ca3af', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px' }}>
                취소
              </button>
              <button onClick={handleSave} disabled={saving}
                style={{ padding: '6px 14px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '3px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: 500 }}>
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
      {copyMenu.menu}
    </div>
  );
};

export default StrategyManagement;
